import { hash32, type ModeId } from '@vm/shared';
import { M } from '../palette';
import { MapBuilder, planks, solid, yawToward, type Brush } from './builder';
import { emptyMeta, type MapDef, type MapMeta, type MapTheme } from './types';

/**
 * Maps built from a TRACED layout: the floor plan of a well-known map (read from an overview image by tools/trace-map.py), rebuilt in
 * our voxel style. The plan keeps what makes those maps work, the proportions between lanes, rooms and cover, and everything else
 * (materials, wall heights, windows, props) is ours. A traced layout is a grid of cells: `#` wall, `.` floor, `c` low cover.
 */
export interface Zone {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
}

export interface TracedLayout {
  /** Metres per cell. */
  cell: number;
  width: number;
  height: number;
  /** Team spawn areas (green rectangles on the source image). */
  spawns: Zone[];
  /** Objective areas (orange / red rectangles on the source image). */
  sites: Zone[];
  /** Cells in a ring this wide around the plan are perimeter wall and never join a building (a plan drawn inside its own outer wall). */
  border?: number;
  /** Run-length encoded rows: `12#` is twelve wall cells. */
  rows: string[];
}

export interface TracedStyle {
  /** Floor materials, mixed in patches. */
  floor: number[];
  /** Floor next to a wall (dirt, dust, grime). */
  floorEdge: number;
  /** Building wall styles; each building gets one. */
  walls: Brush[];
  /** Perimeter wall. */
  perimeter: Brush;
  trim: number;
  /** Window panels on building faces. */
  window: number;
  /** Cover: crates and barrels. */
  crate: Brush;
  /** Heights of buildings in voxels (picked per building). */
  heights: number[];
  /** Voxels the perimeter stands above the ground. */
  perimeterHeight: number;
}

export interface TracedSpec {
  id: string;
  name: string;
  description: string;
  layout: TracedLayout;
  style: TracedStyle;
  theme: MapTheme;
  modes: ModeId[];
  seed: number;
  version?: number;
}

const G = 32;
const MARGIN = 20;
/** Voxels per layout cell (0.5 m). */
const CV = 5;
/** Cover height in voxels: 1.4 m, just over a jump. */
const COVER_H = 14;

const N4: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1]];

export function decodeRows(l: TracedLayout): Uint8Array {
  const g = new Uint8Array(l.width * l.height);
  l.rows.forEach((row, z) => {
    let x = 0;
    let n = '';
    for (const ch of row) {
      if (ch >= '0' && ch <= '9') {
        n += ch;
        continue;
      }
      const run = n ? Number(n) : 1;
      n = '';
      const v = ch === '#' ? 0 : ch === '.' ? 1 : 2;
      for (let i = 0; i < run; i++) g[z * l.width + x++] = v;
    }
    if (x !== l.width) throw new Error(`traced row ${z} has ${x} cells, expected ${l.width}`);
  });
  return g;
}

/** 4-connected components of cells where `pick` is true; returns a label per cell (0 = none) and the component count. */
function label(w: number, h: number, pick: (i: number) => boolean): { lab: Int32Array; n: number } {
  const lab = new Int32Array(w * h);
  let n = 0;
  const stack: number[] = [];
  for (let s = 0; s < w * h; s++) {
    if (lab[s] !== 0 || !pick(s)) continue;
    n++;
    lab[s] = n;
    stack.push(s);
    while (stack.length) {
      const i = stack.pop()!;
      const x = i % w;
      const z = (i / w) | 0;
      for (const [dx, dz] of N4) {
        const nx = x + dx;
        const nz = z + dz;
        if (nx < 0 || nz < 0 || nx >= w || nz >= h) continue;
        const j = nz * w + nx;
        if (lab[j] === 0 && pick(j)) {
          lab[j] = n;
          stack.push(j);
        }
      }
    }
  }
  return { lab, n };
}

/** Distance (in cells, 4-connected) from every cell to the nearest wall cell. */
function wallDistance(w: number, h: number, g: Uint8Array): Int32Array {
  const d = new Int32Array(w * h).fill(1 << 20);
  const q: number[] = [];
  for (let i = 0; i < w * h; i++)
    if (g[i] === 0) {
      d[i] = 0;
      q.push(i);
    }
  for (let k = 0; k < q.length; k++) {
    const i = q[k];
    const x = i % w;
    const z = (i / w) | 0;
    for (const [dx, dz] of N4) {
      const nx = x + dx;
      const nz = z + dz;
      if (nx < 0 || nz < 0 || nx >= w || nz >= h) continue;
      const j = nz * w + nx;
      if (d[j] > d[i] + 1) {
        d[j] = d[i] + 1;
        q.push(j);
      }
    }
  }
  return d;
}

export function tracedMap(spec: TracedSpec): MapDef {
  const L = spec.layout;
  if (L.cell !== 0.5) throw new Error('traced layouts use 0.5 m cells');
  const sx = Math.ceil((L.width * CV + MARGIN * 2) / 32) * 32;
  const sz = Math.ceil((L.height * CV + MARGIN * 2) / 32) * 32;
  const style = spec.style;

  function build(b: MapBuilder): MapMeta {
    const grid = decodeRows(L);
    const W = L.width;
    const H = L.height;
    const ox = Math.floor((sx - W * CV) / 2);
    const oz = Math.floor((sz - H * CV) / 2);
    const at = (x: number, z: number): number => (x < 0 || z < 0 || x >= W || z >= H ? 0 : grid[z * W + x]);

    // Ground everywhere, then the floor cells get patches of the style's floor materials. ------------
    b.ground(G, style.floor[0], M('dirt_2'), M('rock_1'));
    const dist = wallDistance(W, H, grid);
    for (let cz = 0; cz < H; cz++)
      for (let cx = 0; cx < W; cx++) {
        if (grid[cz * W + cx] === 0) continue;
        // Patches of 2 x 2 cells; the strip next to a wall is dirt.
        const pick = style.floor[hash32(cx >> 1, cz >> 1, spec.seed) % style.floor.length];
        const edge = dist[cz * W + cx] <= 1;
        for (let dz = 0; dz < CV; dz++)
          for (let dx = 0; dx < CV; dx++) {
            const x = ox + cx * CV + dx;
            const z = oz + cz * CV + dz;
            const v = edge && hash32(x, z, 7) % 3 !== 0 ? style.floorEdge : pick;
            b.set(x, G - 1, z, v);
          }
      }

    // Walls: every connected wall region is either the perimeter (touches the edge of the plan) or a building. -----
    const bd = L.border ?? 0;
    const inBand = (i: number): boolean => {
      const x = i % W;
      const z = (i / W) | 0;
      return x < bd || z < bd || x >= W - bd || z >= H - bd;
    };
    const { lab, n } = label(W, H, (i) => grid[i] === 0 && !inBand(i));
    const outer = new Uint8Array(n + 1);
    const size = new Int32Array(n + 1);
    for (let i = 0; i < W * H; i++) {
      const c = lab[i];
      if (c === 0) continue;
      size[c]++;
      const x = i % W;
      const z = (i / W) | 0;
      if (x === 0 || z === 0 || x === W - 1 || z === H - 1) outer[c] = 1;
    }
    const heightOf = (c: number): number => (outer[c] ? style.perimeterHeight : size[c] < 30 ? 32 : style.heights[hash32(c, spec.seed, 3) % style.heights.length]);
    const brushOf = (c: number): Brush => (outer[c] ? style.perimeter : style.walls[hash32(c, spec.seed, 5) % style.walls.length]);
    // The solid slab behind the plan (everything outside it) so the edge of the world is a wall too.
    b.box(0, G, 0, sx, G + style.perimeterHeight, oz, style.perimeter);
    b.box(0, G, oz + H * CV, sx, G + style.perimeterHeight, sz, style.perimeter);
    b.box(0, G, oz, ox, G + style.perimeterHeight, oz + H * CV, style.perimeter);
    b.box(ox + W * CV, G, oz, sx, G + style.perimeterHeight, oz + H * CV, style.perimeter);
    for (let cz = 0; cz < H; cz++)
      for (let cx = 0; cx < W; cx++) {
        const c = lab[cz * W + cx];
        if (c === 0 && !(bd > 0 && grid[cz * W + cx] === 0 && inBand(cz * W + cx))) continue;
        if (c === 0) {
          const bx = ox + cx * CV;
          const bz = oz + cz * CV;
          b.box(bx, G, bz, bx + CV, G + style.perimeterHeight, bz + CV, style.perimeter);
          continue;
        }
        const h = heightOf(c);
        const wb = brushOf(c);
        const x0 = ox + cx * CV;
        const z0 = oz + cz * CV;
        b.box(x0, G, z0, x0 + CV, G + h, z0 + CV, wb);
        // Plinth and top course.
        b.box(x0, G, z0, x0 + CV, G + 3, z0 + CV, style.trim);
        b.box(x0, G + h - 2, z0, x0 + CV, G + h, z0 + CV, style.trim);
        // Window panels on faces that look onto the floor (every building taller than 4 m).
        if (!outer[c] && h >= 40 && hash32(c, spec.seed, 9) % 3 !== 0) {
          for (let d = 0; d < 4; d++) {
            const [dx, dz] = N4[d];
            if (at(cx + dx, cz + dz) === 0) continue;
            for (let k = 0; k < CV; k++) {
              const along = d < 2 ? z0 + k : x0 + k;
              if (along % 28 >= 12) continue;
              const x = d === 0 ? x0 + CV - 1 : d === 1 ? x0 : x0 + k;
              const z = d === 2 ? z0 + CV - 1 : d === 3 ? z0 : z0 + k;
              b.box(x, G + 20, z, x + 1, G + 32, z + 1, style.window);
            }
          }
        }
      }

    // Cover: crates and barrels. Each blob is one prop, one block high. ---------------------------------
    const cov = label(W, H, (i) => grid[i] === 2);
    const bounds = new Map<number, [number, number, number, number, number]>();
    for (let i = 0; i < W * H; i++) {
      const c = cov.lab[i];
      if (c === 0) continue;
      const x = i % W;
      const z = (i / W) | 0;
      const e = bounds.get(c);
      if (!e) bounds.set(c, [x, z, x + 1, z + 1, 1]);
      else {
        e[0] = Math.min(e[0], x);
        e[1] = Math.min(e[1], z);
        e[2] = Math.max(e[2], x + 1);
        e[3] = Math.max(e[3], z + 1);
        e[4]++;
      }
    }
    for (const [c, [x0, z0, x1, z1]] of bounds) {
      const vx0 = ox + x0 * CV;
      const vz0 = oz + z0 * CV;
      const vx1 = ox + x1 * CV;
      const vz1 = oz + z1 * CV;
      const r = hash32(c, spec.seed, 11) % 4;
      if (x1 - x0 <= 2 && z1 - z0 <= 2 && r === 0) {
        b.barrel((vx0 + vx1) >> 1, G, (vz0 + vz1) >> 1, M(['paint_red', 'paint_orange', 'paint_yellow', 'paint_teal'][hash32(c, 13) % 4]));
        continue;
      }
      b.box(vx0, G, vz0, vx1, G + COVER_H, vz1, style.crate);
      b.box(vx0, G + COVER_H - 1, vz0, vx1, G + COVER_H, vz1, M('planks_dark_1'));
      b.box(vx0, G, vz0, vx1, G + 1, vz1, M('planks_dark_1'));
    }

    // Metadata ------------------------------------------------------------------------------------------
    const gy = G / 10;
    const meta = emptyMeta(gy, sx / 20, sz / 20);
    const toX = (cx: number): number => (ox + cx * CV) / 10;
    const toZ = (cz: number): number => (oz + cz * CV) / 10;
    const free = (cx: number, cz: number, r = 2): boolean => dist[cz * W + cx] > r && grid[cz * W + cx] === 1;

    // Team spawn areas: the green boxes split into two groups by distance to the two that are farthest apart.
    const zones = L.spawns;
    let a = 0;
    let bIdx = Math.min(1, zones.length - 1);
    let far = -1;
    for (let i = 0; i < zones.length; i++)
      for (let j = i + 1; j < zones.length; j++) {
        const d = (zones[i].x0 - zones[j].x0) ** 2 + (zones[i].z0 - zones[j].z0) ** 2;
        if (d > far) {
          far = d;
          a = i;
          bIdx = j;
        }
      }
    const cx = (z: Zone): number => (z.x0 + z.x1) / 2;
    const cz = (z: Zone): number => (z.z0 + z.z1) / 2;
    const seedA = zones[a];
    const seedB = zones[bIdx];
    const teamOf = (z: Zone): number => ((cx(z) - cx(seedA)) ** 2 + (cz(z) - cz(seedA)) ** 2 <= (cx(z) - cx(seedB)) ** 2 + (cz(z) - cz(seedB)) ** 2 ? 0 : 1);
    // Team 0 is whichever group has the smaller x.
    const groupX = [0, 0];
    const groupN = [0, 0];
    for (const z of zones) {
      groupX[teamOf(z)] += cx(z);
      groupN[teamOf(z)]++;
    }
    const flip = groupX[0] / Math.max(1, groupN[0]) > groupX[1] / Math.max(1, groupN[1]);
    const team = (z: Zone): number => (flip ? 1 - teamOf(z) : teamOf(z));

    const taken: [number, number][] = [];
    const farEnough = (x: number, z: number, d: number): boolean => taken.every(([tx, tz]) => (tx - x) ** 2 + (tz - z) ** 2 >= d * d);
    const centre = { x: W / 2, z: H / 2 };
    const teamCentre: { x: number; z: number }[] = [{ x: 0, z: 0 }, { x: 0, z: 0 }];
    const teamSpawns: [number, number][][] = [[], []];
    for (let t = 0; t < 2; t++) {
      const cells: [number, number][] = [];
      for (const z of zones.filter((q) => team(q) === t))
        for (let zz = Math.ceil(z.z0); zz < Math.floor(z.z1); zz++)
          for (let xx = Math.ceil(z.x0); xx < Math.floor(z.x1); xx++) if (xx >= 0 && zz >= 0 && xx < W && zz < H && free(xx, zz, 1)) cells.push([xx, zz]);
      cells.sort((p, q) => hash32(p[0], p[1], spec.seed, 21) - hash32(q[0], q[1], spec.seed, 21));
      const mine: [number, number][] = [];
      for (const [xx, zz] of cells) {
        if (mine.length >= 8) break;
        if (mine.every(([mx, mz]) => (mx - xx) ** 2 + (mz - zz) ** 2 >= 9)) mine.push([xx, zz]);
      }
      teamSpawns[t] = mine;
      teamCentre[t] = mine.length ? { x: mine.reduce((s, p) => s + p[0], 0) / mine.length, z: mine.reduce((s, p) => s + p[1], 0) / mine.length } : centre;
    }
    for (let t = 0; t < 2; t++) {
      const foe = teamCentre[1 - t];
      for (const [xx, zz] of teamSpawns[t]) {
        meta.spawns.push({ team: t, x: toX(xx) + 0.25, y: gy, z: toZ(zz) + 0.25, yaw: yawToward(xx, zz, foe.x, foe.z) });
        taken.push([xx, zz]);
      }
    }
    // Keep the teams the same size (the map test requires it).
    const n0 = meta.spawns.filter((s) => s.team === 0).length;
    const n1 = meta.spawns.filter((s) => s.team === 1).length;
    if (n0 !== n1) {
      const drop = n0 > n1 ? 0 : 1;
      for (let k = Math.abs(n0 - n1); k > 0; k--) {
        const i = meta.spawns.map((s) => s.team).lastIndexOf(drop);
        meta.spawns.splice(i, 1);
      }
    }

    // Free-for-all spawns: spread over the open floor, away from the team areas.
    const floorCells: [number, number][] = [];
    for (let zz = 2; zz < H - 2; zz += 2) for (let xx = 2; xx < W - 2; xx += 2) if (free(xx, zz, 3)) floorCells.push([xx, zz]);
    floorCells.sort((p, q) => hash32(p[0], p[1], spec.seed, 23) - hash32(q[0], q[1], spec.seed, 23));
    const ffa: [number, number][] = [];
    for (const [xx, zz] of floorCells) {
      if (ffa.length >= 20) break;
      if (ffa.every(([fx, fz]) => (fx - xx) ** 2 + (fz - zz) ** 2 >= 14 * 14) && farEnough(xx, zz, 16)) ffa.push([xx, zz]);
    }
    for (let d = 12; ffa.length < 18 && d >= 6; d -= 2)
      for (const [xx, zz] of floorCells) {
        if (ffa.length >= 20) break;
        if (ffa.every(([fx, fz]) => (fx - xx) ** 2 + (fz - zz) ** 2 >= d * d) && farEnough(xx, zz, d)) ffa.push([xx, zz]);
      }
    for (const [xx, zz] of ffa) meta.spawns.push({ team: -1, x: toX(xx) + 0.25, y: gy, z: toZ(zz) + 0.25, yaw: yawToward(xx, zz, centre.x, centre.z) });

    // Objectives: the marked sites, plus the middle of the map. Flags sit in the team areas, clear of the spawns.
    const siteCells: [number, number][] = [];
    const nearestFree = (px: number, pz: number): [number, number] => {
      let best: [number, number] = [Math.round(px), Math.round(pz)];
      let bd = Infinity;
      for (let zz = Math.max(1, Math.round(pz) - 14); zz < Math.min(H - 1, Math.round(pz) + 14); zz++)
        for (let xx = Math.max(1, Math.round(px) - 14); xx < Math.min(W - 1, Math.round(px) + 14); xx++) {
          if (!free(xx, zz, 3)) continue;
          const d = (xx - px) ** 2 + (zz - pz) ** 2;
          if (d < bd) {
            bd = d;
            best = [xx, zz];
          }
        }
      return best;
    };
    for (const s of L.sites) siteCells.push(nearestFree(cx(s), cz(s)));
    siteCells.push(nearestFree(centre.x, centre.z));
    siteCells.slice(0, 3).forEach(([xx, zz], i) => meta.points.push({ id: 'ABC'[i], name: ['Site A', 'Site B', 'Mid'][i] ?? `Point ${i}`, x: toX(xx) + 0.25, y: gy, z: toZ(zz) + 0.25, r: 3.5 }));
    // Mark each objective area on the floor with a yellow-and-black ring.
    for (const p of meta.points) {
      const px = Math.round(p.x * 10);
      const pz = Math.round(p.z * 10);
      for (let dz = -35; dz <= 35; dz++)
        for (let dx = -35; dx <= 35; dx++) {
          const d2 = dx * dx + dz * dz;
          if (d2 < 33 * 33 || d2 > 35 * 35) continue;
          if (b.w.get(px + dx, G - 1, pz + dz) !== 0 && b.w.get(px + dx, G, pz + dz) === 0) b.set(px + dx, G - 1, pz + dz, (dx + dz) % 6 < 3 ? M('road_yellow') : M('paint_black'));
        }
    }
    for (let t = 0; t < 2; t++) {
      const [fx, fz] = nearestFree(teamCentre[t].x, teamCentre[t].z);
      // The flag stands a few metres off the spawns.
      let best: [number, number] = [fx, fz];
      let bd = -1;
      for (let zz = Math.max(1, fz - 16); zz < Math.min(H - 1, fz + 16); zz++)
        for (let xx = Math.max(1, fx - 16); xx < Math.min(W - 1, fx + 16); xx++) {
          if (!free(xx, zz, 3)) continue;
          const dm = Math.min(...(teamSpawns[t].length ? teamSpawns[t] : [[fx, fz] as [number, number]]).map(([sx2, sz2]) => (sx2 - xx) ** 2 + (sz2 - zz) ** 2));
          if (dm >= 144 && (bd < 0 || dm < bd)) {
            bd = dm;
            best = [xx, zz];
          }
        }
      meta.flags.push({ team: t, x: toX(best[0]) + 0.25, y: gy, z: toZ(best[1]) + 0.25 });
    }

    // Pickups: health and armor on open floor, far from spawns and from each other.
    const picks: [number, number][] = [];
    const spots = floorCells.filter(([xx, zz]) => free(xx, zz, 3));
    for (let k = 0; k < 8; k++) {
      let best: [number, number] | null = null;
      let bd = -1;
      for (const [xx, zz] of spots) {
        const ds = Math.min(...taken.concat(picks).map(([tx, tz]) => (tx - xx) ** 2 + (tz - zz) ** 2));
        if (ds > bd && ds >= 100) {
          bd = ds;
          best = [xx, zz];
        }
      }
      if (best) picks.push(best);
    }
    picks.forEach(([xx, zz], i) => meta.pickups.push({ kind: i % 4 === 3 ? 'armor' : 'health', x: toX(xx) + 0.25, y: gy, z: toZ(zz) + 0.25 }));

    // Keep the voxels around every spawn clear and make sure there is floor under it.
    for (const s of meta.spawns) {
      const x = Math.floor(s.x * 10);
      const z = Math.floor(s.z * 10);
      b.clear(x - 3, G, z - 3, x + 4, G + 18, z + 4);
      if (b.w.get(x, G - 1, z) === 0) b.set(x, G - 1, z, solid([M('stone_1')]) as number);
    }
    void planks;
    // The perimeter wall and the slab behind it are out of play: nothing that moves may stand on them or beyond them.
    b.limitPlayRim(G + 20);
    return meta;
  }

  return {
    id: spec.id,
    name: spec.name,
    description: spec.description,
    size: [sx, 192, sz],
    modes: spec.modes,
    players: [4, 12],
    seed: spec.seed,
    version: (spec.version ?? 1) + 1,
    theme: spec.theme,
    build,
  };
}
